import type { ForwardedRef } from "react";
import React, { forwardRef, useCallback, useState } from "react";

import { IS_APP } from "@/__main__/constants.mjs";
import { classNames } from "@/util/class-names.mjs";
import globals from "@/util/global-whitelist.mjs";
import { useClientLayoutEffect } from "@/util/use-client-layout-effect.mjs";
import useForwardedRef from "@/util/use-forwarded-ref.mjs";

import { cssDialog } from "./styles.mjs";

// NOTE: Using the native <dialog> element for now.
// Though about using the Headless <Dialog> because
// it can be a little nicer, but this is works
// and has its own benefits.

// Native: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
// Headless: https://headlessui.dev/react/dialog

export const ModalCloseBtn = ({
  children,
  ...restProps
}: React.ComponentPropsWithoutRef<"button">) => (
  <form method="dialog">
    <button {...restProps}>{children}</button>
  </form>
);

type DialogProps = React.ComponentPropsWithoutRef<"dialog"> & {
  // these timings are to ensure the modal always exists at the time of callback
  // onModalShow fires AFTER the modal is opened.
  onModalShow?: () => void;
  // onModalClose fires AFTER the modal is closed.
  onModalClose?: () => void;
  // Make the modal unclosable by normal methods
  blockClose?: boolean;
};

export const Modal = forwardRef<HTMLDialogElement, DialogProps>(function Modal(
  { onModalShow, onModalClose, children, blockClose, className, ...restProps },
  fwdRef: ForwardedRef<HTMLDialogElement>,
) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useForwardedRef(fwdRef);

  const dialogClickHandler: React.MouseEventHandler<HTMLDialogElement> =
    useCallback(
      (e) => {
        if (blockClose) return;
        if (!(e.target instanceof HTMLDialogElement)) return;
        e.target.close();
      },
      [blockClose],
    );

  useClientLayoutEffect(() => {
    if (!(blockClose && ref.current)) return;
    const elem = ref.current;

    const handler = (event: Event) => {
      event.preventDefault();
    };

    elem.addEventListener("cancel", handler);

    return () => {
      elem.removeEventListener("cancel", handler);
    };
  }, [blockClose, ref]);

  useClientLayoutEffect(() => {
    if (!ref.current) return;

    const obs = new MutationObserver(() => {
      if (!ref.current) return;
      const open = ref.current.open;

      setIsOpen(open);

      if (open) {
        onModalShow?.();
      } else {
        onModalClose?.();
      }
    });

    obs.observe(ref.current, {
      attributes: true,
      attributeFilter: ["open"],
    });

    // Handle the case where the modal *starts* open
    if (ref.current.open) {
      setIsOpen(true);
      onModalShow?.();
    }

    // handles a click outside the modal and modal backdrop.
    // modal behavior is that this will fire on the HTML element when clicked outside
    const outsideClickHandler = (e: MouseEvent) => {
      if (!(e.target instanceof HTMLElement)) return;
      if (e.target.tagName === "HTML" && !blockClose) ref.current?.close();
    };
    globals.document.documentElement.addEventListener(
      "click",
      outsideClickHandler,
      { passive: true },
    );
    return () => {
      obs.disconnect();
      globals.document.documentElement.removeEventListener(
        "click",
        outsideClickHandler,
      );
    };
  }, [blockClose, onModalClose, onModalShow, ref]);

  return (
    <dialog
      ref={ref}
      onClick={dialogClickHandler}
      {...classNames(
        className,
        cssDialog(),
        IS_APP ? "title-bar" : null,
        blockClose ? "block-close" : null,
      )}
      {...restProps}
    >
      {isOpen && children}
    </dialog>
  );
});
